import type { Environment } from '@flatfile/api-internal/api'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownInput,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Icon,
  IconName,
  Input,
  Label,
  Text,
  useToast,
} from '@flatfile/baseline'
import { copyToClipboard } from '@flatfile/shared-ui'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppHTTPService } from '../../api/http-service'
import { useActiveEnvironment, useHasEnvironmentsAccess } from '../../hooks/useEnvironments'
import { useIdentity } from '../../hooks/useIdentity'
import { useCollection } from '../../resources/hooks/useCollection'
import useResource from '../../resources/hooks/useResource'
import getCollection from '../../resources/support/getCollection'
import { logger } from '../../utils/logging'

// Separate Item component to properly handle hooks
const EnvironmentItem = ({
  id,
  selected,
  onSelect,
  closeDropdown,
}: {
  id: string
  selected: boolean
  onSelect: (env: Environment) => void
  closeDropdown: () => void
}) => {
  const env = useResource('ENVIRONMENTS', id)
  const navigate = useNavigate()
  const [nameIconState, setNameIconState] = useState<{ name: IconName; color: string }>({
    name: 'square-behind-square-1',
    color: 'muted',
  })
  const [idIconState, setIdIconState] = useState<{ name: IconName; color: string }>({
    name: 'square-behind-square-1',
    color: 'muted',
  })

  if (!env) return null

  const handleCopyName = async () => {
    await copyToClipboard(env.name)
    setNameIconState({ name: 'checkmark-2', color: 'success' })
    setTimeout(() => {
      setNameIconState({ name: 'square-behind-square-1', color: 'muted' })
    }, 1500)
  }

  const handleCopyId = async () => {
    await copyToClipboard(env.id)
    setIdIconState({ name: 'checkmark-2', color: 'success' })
    setTimeout(() => {
      setIdIconState({ name: 'square-behind-square-1', color: 'muted' })
    }, 1500)
  }

  const handleSelect = () => {
    if (!selected) {
      onSelect(env)
      closeDropdown()
    }
  }

  // Use environment only handler
  const handleUseEnvironment = () => {
    if (!selected) {
      onSelect(env)
      closeDropdown()
    }
  }

  // Navigation handlers
  const navigateToAsyncJobs = () => {
    if (!selected) onSelect(env)
    navigate('/jobs')
  }
  const navigateToLogs = () => {
    if (!selected) onSelect(env)
    navigate('/logs')
  }
  const navigateToAgents = () => {
    if (!selected) onSelect(env)
    navigate('/agents')
  }
  const navigateToApiKeys = () => {
    if (!selected) onSelect(env)
    navigate('keys-and-secrets')
  }
  const navigateToDocs = () => window.open('https://flatfile.com/docs/overview', '_blank')

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger showIconOnHover onClick={handleSelect}>
        <div className='flex items-center gap-2 flex-grow justify-between w-full cursor-pointer'>
          <div className='flex items-center gap-2 max-w-[140px] flex-shrink overflow-hidden'>
            <span
              className={
                !selected ? 'text-sm font-normal truncate text-primary' : 'text-sm font-medium truncate text-primary'
              }
              title={env.name}
            >
              {env.name}
            </span>
            {selected && (
              <span className='text-xs font-medium bg-gray-200 px-2 py-0.5 rounded whitespace-nowrap'>Current</span>
            )}
          </div>
          <Text color='interactive-state-selected-pressed' className='flex-shrink-0'>
            {env.isProd ? 'PROD' : 'DEV'}
          </Text>
        </div>
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className='w-[254px]'>
          <div className='p-1'>
            <div className='mb-1'>
              <div className='flex items-baseline gap-3 justify-end'>
                <Text className='whitespace-nowrap text-right font-normal text-muted' variant='subheadline'>
                  Name
                </Text>
                <div className='relative w-[186px]'>
                  <Text variant='body' className='truncate pr-8'>
                    {env.name}
                  </Text>
                  <Button
                    className='absolute right-2 top-1/2 -translate-y-1/2 p-0'
                    variant='ghost'
                    onClick={handleCopyName}
                    icon={nameIconState.name}
                    iconProps={{ size: 12, color: nameIconState.color }}
                  ></Button>
                </div>
              </div>
            </div>
            <div>
              <div className='flex items-baseline gap-3 justify-end'>
                <Text className='whitespace-nowrap text-right font-normal text-muted' variant='subheadline'>
                  ID
                </Text>
                <div className='relative w-[186px] items-center'>
                  <Text variant='body' className='truncate pr-8'>
                    {env.id}
                  </Text>
                  <Button
                    className='absolute right-2 top-1/2 -translate-y-1/2 p-0'
                    variant='ghost'
                    onClick={handleCopyId}
                    icon={idIconState.name}
                    iconProps={{ size: 12, color: idIconState.color }}
                  ></Button>
                </div>
              </div>
            </div>
          </div>

          <DropdownMenuSeparator className='m-0 mb-2' />
          <DropdownMenuGroup className='gap-2'>
            <DropdownMenuItem
              className='flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-gray-100 text-primary'
              onClick={handleUseEnvironment}
            >
              <Icon name='circle-check' size={16} color='text-default' />
              <span>Use This Environment</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className='flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-gray-100 text-primary'
              onClick={navigateToAsyncJobs}
            >
              <Icon name='arrow-rotate-right-left' size={16} color='text-default' />
              <span>Async Jobs</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              className='flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-gray-100 text-primary'
              onClick={navigateToLogs}
            >
              <Icon name='square-checklist-bell' size={16} color='text-default' />
              <span>Logs</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              className='flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-gray-100 text-primary'
              onClick={navigateToAgents}
            >
              <Icon name='code' size={16} color='text-default' />
              <span>Agents</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              className='flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-gray-100 text-primary'
              onClick={navigateToApiKeys}
            >
              <Icon name='key-2' size={16} color='text-default' />
              <span>API Keys & Secrets</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator className='m-0 my-2' />

            <DropdownMenuItem
              className='flex items-center justify-between px-2 py-1.5 text-sm rounded hover:bg-gray-100 text-primary'
              onClick={navigateToDocs}
            >
              <div className='flex items-center gap-2'>
                <Icon name='file-text' size={16} color='text-default' />
                <span>Docs & Resources</span>
              </div>
              <Icon name='square-arrow-top-right' size={16} color='text-default' />
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}

export const EnvironmentSwitcher = () => {
  const { identity, hasPermission } = useIdentity()
  const hasEnvAccess = useHasEnvironmentsAccess()
  const { toast } = useToast()

  // Use useCollection to get the actual Environment objects directly
  const allEnvironments = useCollection('ENVIRONMENTS')
  const prodEnvironments = allEnvironments.filter((env) => env.isProd === true)
  const nonProdEnvironments = allEnvironments.filter((env) => env.isProd === false)

  const { environment: activeEnvironment, setActiveEnvironment } = useActiveEnvironment()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Clear search term when dropdown closes
  const handleDropdownOpenChange = (isOpen: boolean) => {
    setDropdownOpen(isOpen)
    if (!isOpen) {
      setSearchTerm('')
    }
  }

  // Form state
  const [name, setName] = useState('')
  const [mode, setMode] = useState('development')
  const [nameError, setNameError] = useState<string | null>(null)
  const [modeError, setModeError] = useState<string | null>(null)

  const validateName = (value: string) => {
    if (!value || value.trim() === '') {
      return 'Environment name is required'
    }
    if (value.length < 3) {
      return 'Name must be at least three characters'
    }
    if (!/^[a-z0-9_-]{3,}$/.test(value)) {
      return 'Name must contain only lowercase letters, numbers, underscores, and hyphens'
    }
    return null
  }

  const validateMode = (value: string) => {
    if (!value) {
      return 'Billing mode is required'
    }
    return null
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setName(value)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleModeChange = (values: (string | number | boolean)[]) => {
    if (values.length > 0) {
      setMode(values[0] as string)
      setModeError(null)
    } else {
      setModeError('Billing mode is required')
    }
  }

  const submit = useCallback(() => {
    const nameValidationError = validateName(name)
    const modeValidationError = validateMode(mode)

    if (nameValidationError) {
      setNameError(nameValidationError)
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: nameValidationError,
      })
      return
    }

    if (modeValidationError) {
      setModeError(modeValidationError)
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: modeValidationError,
      })
      return
    }

    AppHTTPService.client.environments
      .create({ name, isProd: mode === 'production' })
      .then((env) => {
        getCollection('ENVIRONMENTS').append(env.data)
        setActiveEnvironment(env.data.id)
        setDialogOpen(false)
        setDropdownOpen(false)
        setName('')
        setMode('development')
        setNameError(null)
        setModeError(null)
      })
      .catch((error) => {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to create environment',
        })
        logger.error('Failed to create environment', error)
      })
  }, [name, mode, setActiveEnvironment, toast])

  const activateEnvironment = useCallback(
    (environment: Environment) => {
      setActiveEnvironment(environment.id)
    },
    [setActiveEnvironment]
  )

  const openDialog = () => {
    setDialogOpen(true)
    setDropdownOpen(false)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setName('')
    setMode('development')
    setNameError(null)
    setModeError(null)
  }

  // Filter environments based on search term
  const filteredNonProdEnvIds = nonProdEnvironments
    .filter((env) => env.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .map((env) => env.id)

  const filteredProdEnvIds = prodEnvironments
    .filter((env) => env.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .map((env) => env.id)

  // Create a combined array of environment IDs with the active environment at the top
  const getOrderedEnvironmentIds = () => {
    const activeEnvId = activeEnvironment.id
    const allFilteredIds = [...filteredNonProdEnvIds, ...filteredProdEnvIds]

    // If the active environment is in the filtered list
    if (allFilteredIds.includes(activeEnvId)) {
      // Remove the active environment from both arrays
      const remainingNonProdIds = filteredNonProdEnvIds.filter((id) => id !== activeEnvId)
      const remainingProdIds = filteredProdEnvIds.filter((id) => id !== activeEnvId)

      // Return with active environment first, then the rest
      return [activeEnvId, ...remainingNonProdIds, ...remainingProdIds]
    }

    return allFilteredIds
  }

  const billingModeOptions = [
    { label: 'production', value: 'production' },
    { label: 'development', value: 'development' },
  ]

  return hasEnvAccess ? (
    <>
      {/* Main Environments Dropdown */}
      <DropdownMenu open={dropdownOpen} onOpenChange={handleDropdownOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className='!w-fit !max-w-[150px] flex items-center align-baseline px-2 hover:bg-interactive-state-selected-enabled -mr-3'
            title={activeEnvironment.name}
            icon='chevron-grabber-vertical'
            iconPlacement='right'
            iconProps={{
              size: 12,
            }}
          >
            <Badge
              className={`text-[10px] px-1.5 py-0.5 ${activeEnvironment.isProd ? 'text-[white]' : ''}`}
              variant={activeEnvironment.isProd ? 'default' : 'outline'}
            >
              {activeEnvironment.isProd ? 'PROD' : 'DEV'}
            </Badge>
            <span className='truncate text-xs font-semibold text-primary'>{activeEnvironment.name}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='w-[260px] p-0' sideOffset={5} align='start'>
          <div className='pt-2'>
            <div className='flex justify-between items-middle h-fit px-3 py-1'>
              <Text variant='callout' color='muted'>
                Environments
              </Text>
              {hasPermission({ resource: identity.account, permissionKey: 'environment:create' }) && (
                <Button
                  size='sm'
                  variant='ghost'
                  onClick={openDialog}
                  aria-label='Create a new Environment'
                  icon='plus-small'
                  className='text-xs p-0 items-middle'
                >
                  <span className='text-primary'>New</span>
                </Button>
              )}
            </div>
            <DropdownMenuSeparator className='m-0 my-1' />
            <div className='px-3'>
              <Input
                placeholder='Search environments...'
                value={searchTerm}
                onChange={handleSearchChange}
                variant='ghost'
                className='w-full outline-none rounded-none'
                size='small'
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <DropdownMenuGroup className='max-h-[160px] overflow-y-auto overflow-x-hidden px-1 pb-4 pr-4 direction-rtl scrollbar-thin'>
            <div className='direction-ltr'>
              {filteredNonProdEnvIds.length === 0 && filteredProdEnvIds.length === 0 ? (
                <div className='py-3 px-2 text-sm text-gray-500 text-center'>No environments match your search</div>
              ) : (
                <>
                  {getOrderedEnvironmentIds().map((id) => (
                    <EnvironmentItem
                      key={id}
                      id={id}
                      selected={activeEnvironment.id === id}
                      onSelect={activateEnvironment}
                      closeDropdown={() => setDropdownOpen(false)}
                    />
                  ))}
                </>
              )}
            </div>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {/* New Environment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(isOpen) => !isOpen && closeDialog()}>
        <DialogContent className='' disablePortal style={{ backdropFilter: 'none' }}>
          <DialogHeader>
            <DialogTitle>New Environment</DialogTitle>
            <Text variant='subheadline' color='text-muted'>
              Environments are isolated entities and are intended to be a safe place to create and test different
              configurations.
            </Text>
          </DialogHeader>
          <div>
            <div className='mb-4'>
              <Label>
                Environment Name<span className='text-destructive'>*</span>
              </Label>
              <Input
                placeholder='your-environment-name'
                value={name}
                onChange={handleNameChange}
                hasError={!!nameError}
              />
              <Text variant='subheadline' color='muted' className='mt-1'>
                lowercase, no spaces. eg 'staging' or 'carls-playground'
              </Text>
              {nameError && (
                <div className='mt-1'>
                  <Text variant='subheadline' className='text-text-sentiment-destructive'>
                    {nameError}
                  </Text>
                </div>
              )}
            </div>

            <div className='mb-4'>
              <Label className='mb-1 block'>
                Billing mode<span className='text-destructive'>*</span>
              </Label>
              <div className='relative'>
                <DropdownInput
                  options={billingModeOptions}
                  defaultValue={[mode]}
                  onValueChange={handleModeChange}
                  placeholder='Select billing mode'
                  className='w-full'
                  modalPopover={false}
                />
              </div>
              {modeError && (
                <div className='mt-1'>
                  <Text variant='subheadline' className='text-text-sentiment-destructive'>
                    {modeError}
                  </Text>
                </div>
              )}
            </div>

            {mode === 'production' ? (
              <div className='flex'>
                <Icon name='circle-info' size={18} className='mr-2' />
                <Text variant='callout' color='muted'>
                  If you are using this for QA or testing, consider using a development environment where usage is free.
                </Text>
              </div>
            ) : (
              <div className='flex'>
                <Icon name='circle-info' size={18} className='mr-2' />
                <Text variant='callout' color='muted'>
                  Development environments are subject to higher rate limits and other constraints.
                </Text>
              </div>
            )}
            <div className='flex mt-4'>
              <Text variant='callout'>
                {mode === 'production'
                  ? "When creating a production environment, remember that all production usage will count towards your account's active billing plan."
                  : 'Usage in development environments is not billed. Development environments are designed for fair use in testing and QA, and not intended for production use.'}
              </Text>
            </div>
            <DialogFooter className='mt-6'>
              <Button type='button' onClick={submit} className='w-full'>
                <span className='text-white'>Create new environment</span>
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  ) : null
}
